import { useState, useEffect, useCallback, useMemo } from "react";
import ErrorBoundary from "../../../components/ErrorBoundary";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faRefresh, faTimes, faFilePdf, faFileCsv, faSpinner, faEye } from '@fortawesome/free-solid-svg-icons';
import './style.css';
import '../../../styles/filters-card.css';
import { API_CONFIG } from '../../../config/api';
import { authService } from '../../../services/authService';
import Button from '../../../components/Button/Button';
import DatePicker from '../../../components/DatePicker/DatePicker';
import Input from '../../../components/input/Input';
import TagInput from '../../../components/input/TagInput';
import Select, { SelectOption } from '../../../components/Select/Select';
import MultiSelect from '../../../components/Select/MultiSelect';
import Pagination from '../../../components/Pagination/Pagination';
import DataTable, { ColumnDef } from '../../../components/DataTable';
import FiltersCard from '../../../components/FiltersCard/FiltersCard';
import { PermissionGate } from '../../../contexts/PermissionGate';
import { useAuthContext } from '../../../contexts/AuthContext';
import { faChartColumn } from '@fortawesome/free-solid-svg-icons';


interface LastroArquivo {
    lastroId: string;
    nomeArquivo: string;
    path: string;
    dataUpload: string;
}

interface LastrosAtivos {
    id: number;
    numeroDocumento: string;
    dataEmissao: string;
    dataAquisicao: string;
    carteira: string;
    nomeFundo: string;
    cnpjCedente: string;
    nomeCedente: string;
    cnpjOriginador: string;
    nomeOriginador: string;
    status: string;
    tipoArmazenamento?: string;
    lastros?: LastroArquivo[];
}

interface PageResponse<T> {
    content: T[];
    totalPages: number;
    totalElements: number;
    number: number;
    size: number;
}

interface Empresa {
    id: number;
    cnpj: string;
    razaoSocial: string;
    nomeFantasia: string;
}

interface FundoCedente {
    fundoId: number;
    cnpjFundo: string;
    nomeFundo: string;
    cedenteId: number;
    cnpjCedente: string;
    nomeCedente: string;
}

interface OriginadorCedente {
    originadorId: number;
    cnpjOriginador: string;
    nomeOriginador: string;
    cedenteId: number;
    cnpjCedente: string;
    nomeCedente: string;
}

interface FundoOriginador {
    fundoId: number;
    cnpjFundo: string;
    nomeFundo: string;
    originadorId: number;
    cnpjOriginador: string;
    nomeOriginador: string;
}

interface Relacionamentos {
    fundoCedentes: FundoCedente[];
    originadorCedentes: OriginadorCedente[];
    fundoOriginadores: FundoOriginador[];
}

interface Filters {
    carteira: string;
    numero_contrato: string[];
    originador: string[];
    cedente: string[];
    dt_aquisicao: string;
    status: string;
}

const intersectSets = (a: Set<string> | null, b: Set<string> | null): Set<string> | null => {
    if (!a && !b) return null;
    if (!a) return b;
    if (!b) return a;
    return new Set(Array.from(a).filter(x => b!.has(x)));
};

const getValidFundos = (rel: Relacionamentos, cedentes: string[], originadores: string[]): Set<string> | null => {
    const byCedente = cedentes.length > 0
        ? new Set(rel.fundoCedentes.filter(fc => cedentes.includes(fc.cnpjCedente)).map(fc => fc.cnpjFundo))
        : null;
    const byOriginador = originadores.length > 0
        ? new Set(rel.fundoOriginadores.filter(fo => originadores.includes(fo.cnpjOriginador)).map(fo => fo.cnpjFundo))
        : null;
    return intersectSets(byCedente, byOriginador);
};

const getValidCedentes = (rel: Relacionamentos, fundo: string, originadores: string[]): Set<string> | null => {
    const byFundo = fundo
        ? new Set(rel.fundoCedentes.filter(fc => fc.cnpjFundo === fundo).map(fc => fc.cnpjCedente))
        : null;
    const byOriginador = originadores.length > 0
        ? new Set(rel.originadorCedentes.filter(oc => originadores.includes(oc.cnpjOriginador)).map(oc => oc.cnpjCedente))
        : null;
    return intersectSets(byFundo, byOriginador);
};

const getValidOriginadores = (rel: Relacionamentos, fundo: string, cedentes: string[]): Set<string> | null => {
    const byFundo = fundo
        ? new Set(rel.fundoOriginadores.filter(fo => fo.cnpjFundo === fundo).map(fo => fo.cnpjOriginador))
        : null;
    const byCedente = cedentes.length > 0
        ? new Set(rel.originadorCedentes.filter(oc => cedentes.includes(oc.cnpjCedente)).map(oc => oc.cnpjOriginador))
        : null;
    return intersectSets(byFundo, byCedente);
};

const buildFundoOptions = (rel: Relacionamentos, validFundos: Set<string> | null): SelectOption[] => {
    const seen = new Map<string, string>();
    rel.fundoCedentes.forEach(fc => { if (!seen.has(fc.cnpjFundo)) seen.set(fc.cnpjFundo, fc.nomeFundo); });
    rel.fundoOriginadores.forEach(fo => { if (!seen.has(fo.cnpjFundo)) seen.set(fo.cnpjFundo, fo.nomeFundo); });
    return [
        { value: '', label: 'Todos os fundos' },
        ...Array.from(seen.entries())
            .filter(([cnpj]) => !validFundos || validFundos.has(cnpj))
            .map(([cnpj, nome]) => ({ value: cnpj, label: nome })),
    ];
};

const buildCedenteOptions = (rel: Relacionamentos, validCedentes: Set<string> | null): SelectOption[] => {
    const seen = new Map<string, string>();
    rel.fundoCedentes.forEach(fc => { if (!seen.has(fc.cnpjCedente)) seen.set(fc.cnpjCedente, fc.nomeCedente); });
    rel.originadorCedentes.forEach(oc => { if (!seen.has(oc.cnpjCedente)) seen.set(oc.cnpjCedente, oc.nomeCedente); });
    return [
        { value: '', label: 'Todos' },
        ...Array.from(seen.entries())
            .filter(([cnpj]) => !validCedentes || validCedentes.has(cnpj))
            .map(([cnpj, nome]) => ({ value: cnpj, label: `${nome} - ${cnpj}` })),
    ];
};

const buildOriginadorOptions = (rel: Relacionamentos, validOriginadores: Set<string> | null): SelectOption[] => {
    const seen = new Map<string, string>();
    rel.fundoOriginadores.forEach(fo => { if (!seen.has(fo.cnpjOriginador)) seen.set(fo.cnpjOriginador, fo.nomeOriginador); });
    rel.originadorCedentes.forEach(oc => { if (!seen.has(oc.cnpjOriginador)) seen.set(oc.cnpjOriginador, oc.nomeOriginador); });
    return [
        { value: '', label: 'Todos' },
        ...Array.from(seen.entries())
            .filter(([cnpj]) => !validOriginadores || validOriginadores.has(cnpj))
            .map(([cnpj, nome]) => ({ value: cnpj, label: nome })),
    ];
};

const applyCascadeForFundo = (prev: Filters, newFundo: string, rel: Relacionamentos): Filters => {
    if (!newFundo) return { ...prev, carteira: newFundo };
    const validCedentes = new Set(rel.fundoCedentes.filter(fc => fc.cnpjFundo === newFundo).map(fc => fc.cnpjCedente));
    const validOriginadores = new Set(rel.fundoOriginadores.filter(fo => fo.cnpjFundo === newFundo).map(fo => fo.cnpjOriginador));
    return {
        ...prev,
        carteira: newFundo,
        cedente: prev.cedente.filter(c => validCedentes.has(c)),
        originador: prev.originador.filter(o => validOriginadores.has(o)),
    };
};

const applyCascadeForCedente = (prev: Filters, newCedentes: string[], rel: Relacionamentos): Filters => {
    if (newCedentes.length === 0) return { ...prev, cedente: newCedentes };
    const validOriginadores = new Set(rel.originadorCedentes.filter(oc => newCedentes.includes(oc.cnpjCedente)).map(oc => oc.cnpjOriginador));
    const validFundos = new Set(rel.fundoCedentes.filter(fc => newCedentes.includes(fc.cnpjCedente)).map(fc => fc.cnpjFundo));
    return {
        ...prev,
        cedente: newCedentes,
        originador: prev.originador.filter(o => validOriginadores.has(o)),
        carteira: prev.carteira && !validFundos.has(prev.carteira) ? '' : prev.carteira,
    };
};

const applyCascadeForOriginador = (prev: Filters, newOriginadores: string[], rel: Relacionamentos): Filters => {
    if (newOriginadores.length === 0) return { ...prev, originador: newOriginadores };
    const validCedentes = new Set(rel.originadorCedentes.filter(oc => newOriginadores.includes(oc.cnpjOriginador)).map(oc => oc.cnpjCedente));
    const validFundos = new Set(rel.fundoOriginadores.filter(fo => newOriginadores.includes(fo.cnpjOriginador)).map(fo => fo.cnpjFundo));
    return {
        ...prev,
        originador: newOriginadores,
        cedente: prev.cedente.filter(c => validCedentes.has(c)),
        carteira: prev.carteira && !validFundos.has(prev.carteira) ? '' : prev.carteira,
    };
};

const Lastros = () => {
    const { permissions } = useAuthContext();
    const canDownload = permissions.includes('DOWNLOAD_BACKINGS');

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [selectedStatuses, setSelectedStatuses] = useState<Record<number, string>>({});
    const [selectingAllIds, setSelectingAllIds] = useState(false);
    const [selectedItem, setSelectedItem] = useState<LastrosAtivos | null>(null);
    const [data, setData] = useState<LastrosAtivos[]>([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [exportLoading, setExportLoading] = useState(false);
    const [exportCsvLoading, setExportCsvLoading] = useState(false);
    const [biLoading, setBiLoading] = useState(false);
    const [selectedLastroIds, setSelectedLastroIds] = useState<string[]>([]);
    const [exportLastroLoading, setExportLastroLoading] = useState(false);

    const [relacionamentos, setRelacionamentos] = useState<Relacionamentos | null>(null);

    const [filters, setFilters] = useState<Filters>({
        carteira: '',
        numero_contrato: [],
        originador: [],
        cedente: [],
        dt_aquisicao: new Date().toISOString().split('T')[0],
        status: ''
    });

    const { fundoOptions, cedenteOptions, originadorOptions } = useMemo(() => {
        if (!relacionamentos) return {
            fundoOptions: [{ value: '', label: 'Todos os fundos' }] as SelectOption[],
            cedenteOptions: [{ value: '', label: 'Todos' }] as SelectOption[],
            originadorOptions: [{ value: '', label: 'Todos' }] as SelectOption[],
        };
        const validFundos = getValidFundos(relacionamentos, filters.cedente, filters.originador);
        const validCedentes = getValidCedentes(relacionamentos, filters.carteira, filters.originador);
        const validOriginadores = getValidOriginadores(relacionamentos, filters.carteira, filters.cedente);
        return {
            fundoOptions: buildFundoOptions(relacionamentos, validFundos),
            cedenteOptions: buildCedenteOptions(relacionamentos, validCedentes),
            originadorOptions: buildOriginadorOptions(relacionamentos, validOriginadores),
        };
    }, [relacionamentos, filters.carteira, filters.cedente, filters.originador]);

    const fetchData = useCallback(async (page = currentPage, size = pageSize, sortBy = sortField, sortDir = sortDirection) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                size: size.toString(),
            });

            if (filters.carteira) params.append('carteira', filters.carteira);
            if (filters.cedente.length) params.append('cedentes', filters.cedente.join(', '));
            if (filters.originador.length) params.append('originadores', filters.originador.join(', '));
            params.append('dataAquisicao', filters.dt_aquisicao);
            if (filters.numero_contrato.length) params.append('numerosContratos', filters.numero_contrato.join(', '));
            if (filters.status) params.append('status', filters.status);
            if (sortBy) params.append('sortBy', sortBy);
            if (sortBy) params.append('sortDirection', sortDir);

            const token = await authService.getValidToken();
            const url = `${API_CONFIG.BASE_URL_JAVA}/api/v1/lastros-contratos?${params.toString()}`;

            const response = await fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Erro ao buscar lastros: ${response.statusText}`);
            }

            const pageData: PageResponse<LastrosAtivos> = await response.json();

            setData(pageData.content ?? []);
            setTotalPages(pageData.totalPages ?? 0);
            setTotalElements(pageData.totalElements ?? 0);
        } catch (error) {
            console.error('Erro ao buscar lastros:', error);
            setData([]);
            setTotalPages(0);
            setTotalElements(0);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, filters, sortField, sortDirection]);

    useEffect(() => { fetchData(currentPage, pageSize); }, [currentPage, pageSize]);

    useEffect(() => {
        const fetchRelacionamentos = async () => {
            try {
                const token = await authService.getValidToken();
                const response = await fetch(`${API_CONFIG.BASE_URL_JAVA}/api/v1/empresas/relacionamentos`, {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                });
                if (response.ok) {
                    const data: Relacionamentos = await response.json();
                    setRelacionamentos(data);
                }
            } catch (err) {
                console.error('Erro ao buscar relacionamentos:', err);
            }
        };
        fetchRelacionamentos();
    }, []);

    const handleSearch = () => {
        setSelectedIds([]);
        setSelectedStatuses({});
        setCurrentPage(0);
        fetchData(0, pageSize);
    };

    const handleFilterChange = (field: string, value: string | string[]) => {
        setFilters(prev => {
            if (!relacionamentos) return { ...prev, [field]: value };
            if (field === 'carteira') return applyCascadeForFundo(prev, value as string, relacionamentos);
            if (field === 'cedente') return applyCascadeForCedente(prev, value as string[], relacionamentos);
            if (field === 'originador') return applyCascadeForOriginador(prev, value as string[], relacionamentos);
            return { ...prev, [field]: value };
        });
    };

    const clearFilters = () => {
        const empty = { carteira: '', numero_contrato: [] as string[], originador: [] as string[], cedente: [] as string[], dt_aquisicao: new Date().toISOString().split('T')[0], status: '' };
        setFilters(empty);
        setSelectedIds([]);
        setSelectedStatuses({});
    };

    const handlePageChange = (page: number) => setCurrentPage(page);

    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(0);
    };

    const handleSort = (field: string) => {
        const newDir: 'asc' | 'desc' = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
        setSortField(field);
        setSortDirection(newDir);
        setCurrentPage(0);
        fetchData(0, pageSize, field, newDir);
    };

    const handleOpenModal = (item: LastrosAtivos) => {
        setSelectedItem(item);
        setSelectedLastroIds([]);
        setModalOpen(true);
    };

    const handleSelectItem = (item: LastrosAtivos) => {
        setSelectedIds(prev =>
            prev.includes(item.id) ? prev.filter(id => id !== item.id) : [...prev, item.id]
        );
        setSelectedStatuses(prev => {
            if (prev[item.id] !== undefined) {
                const next = { ...prev };
                delete next[item.id];
                return next;
            }
            return { ...prev, [item.id]: item.status };
        });
    };

    const handleSelectAllPdf = async () => {
        const allSelectedPdf = selectedIds.length > 0 && selectedIds.length <= 100;
        if (allSelectedPdf) {
            setSelectedIds([]);
            setSelectedStatuses({});
            return;
        }
        try {
            setSelectingAllIds(true);
            const token = await authService.getValidToken();
            const params = new URLSearchParams({ page: '0', size: '100' });
            if (filters.carteira) params.append('carteira', filters.carteira);
            if (filters.cedente.length) params.append('cedentes', filters.cedente.join(', '));
            if (filters.originador.length) params.append('originadores', filters.originador.join(', '));
            params.append('dataAquisicao', filters.dt_aquisicao);
            if (filters.numero_contrato.length) params.append('numerosContratos', filters.numero_contrato.join(', '));
            if (filters.status) params.append('status', filters.status);
            const response = await fetch(
                `${API_CONFIG.BASE_URL_JAVA}/api/v1/lastros-contratos?${params.toString()}`,
                {
                    method: 'GET',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                }
            );
            if (!response.ok) throw new Error('Erro ao buscar IDs para PDF');
            const pageData: PageResponse<LastrosAtivos> = await response.json();
            setSelectedIds(pageData.content.map(item => item.id));
            setSelectedStatuses(Object.fromEntries(pageData.content.map(item => [item.id, item.status])));
        } catch (err) {
            console.error('Erro ao selecionar para PDF:', err);
        } finally {
            setSelectingAllIds(false);
        }
    };

    const handleSelectAll = async () => {
        const allSelected = selectedIds.length === totalElements && totalElements > 0;
        if (allSelected) {
            setSelectedIds([]);
            setSelectedStatuses({});
            return;
        }
        try {
            setSelectingAllIds(true);
            const token = await authService.getValidToken();
            const params = new URLSearchParams({ page: '0', size: totalElements.toString() });
            if (filters.carteira) params.append('carteira', filters.carteira);
            if (filters.cedente.length) params.append('cedentes', filters.cedente.join(', '));
            if (filters.originador.length) params.append('originadores', filters.originador.join(', '));
            params.append('dataAquisicao', filters.dt_aquisicao);
            if (filters.numero_contrato.length) params.append('numerosContratos', filters.numero_contrato.join(', '));
            if (filters.status) params.append('status', filters.status);
            const response = await fetch(
                `${API_CONFIG.BASE_URL_JAVA}/api/v1/lastros-contratos?${params.toString()}`,
                {
                    method: 'GET',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                }
            );
            if (!response.ok) throw new Error('Erro ao buscar todos os IDs');
            const pageData: PageResponse<LastrosAtivos> = await response.json();
            setSelectedIds(pageData.content.map(item => item.id));
            setSelectedStatuses(Object.fromEntries(pageData.content.map(item => [item.id, item.status])));
        } catch (err) {
            console.error('Erro ao selecionar todos:', err);
        } finally {
            setSelectingAllIds(false);
        }
    };

    const exportCsvAll = async () => {
        setExportCsvLoading(true);
        try {
            const token = await authService.getValidToken();
            const body: Record<string, unknown> = {
                carteira: filters.carteira,
                dataAquisicao: filters.dt_aquisicao,
                cedentes: filters.cedente.length ? filters.cedente : undefined,
                originadores: filters.originador.length ? filters.originador : undefined,
                numerosContratos: filters.numero_contrato.length ? filters.numero_contrato : undefined,
                status: filters.status || undefined,
                idsContratos: selectedIds.length > 0 ? selectedIds : undefined,
            };
            const response = await fetch(
                `${API_CONFIG.BASE_URL_JAVA}/api/v1/lastros-contratos/download-excel`,
                {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify(body),
                }
            );
            if (!response.ok) throw new Error('Erro ao exportar CSV');
            const disposition = response.headers.get('content-disposition') ?? '';
            const filenameMatch = disposition.match(/filename="?([^";\s]+)"?/);
            const filename = filenameMatch?.[1] ?? `lastros_${Date.now()}.xlsx`;
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Erro ao exportar CSV:', err);
        } finally {
            setExportCsvLoading(false);
        }
    };

    const formatDate = (dateString: string) => {
        const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
        return new Date(year, month - 1, day).toLocaleDateString('pt-BR');
    };

    const exportLastros = async () => {
        if (!selectedItem) return;
        setExportLastroLoading(true);
        try {
            const token = await authService.getValidToken();
            const idUsuario = localStorage.getItem('id_usuario') ?? undefined;
            const body: Record<string, unknown> = {
                carteira: filters.carteira,
                dataAquisicao: filters.dt_aquisicao,
                ids: [selectedItem.id],
                lastroIds: selectedLastroIds,
                idUsuario: idUsuario,
            };
            const response = await fetch(
                `${API_CONFIG.BASE_URL_JAVA}/api/v1/lastros-contratos/download-lastros`,
                {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify(body),
                }
            );
            if (!response.ok) throw new Error('Erro ao exportar lastros');
            const json = await response.json();
            const downloadUrl = json?.downloadUrl;
            if (!downloadUrl) throw new Error('URL de download não encontrada na resposta');
            window.location.href = downloadUrl;
        } catch (err) {
            console.error('Erro ao exportar lastros:', err);
        } finally {
            setExportLastroLoading(false);
        }
    };

    const exportLastrosPorContrato = async () => {
        setExportLoading(true);
        try {
            const token = await authService.getValidToken();
            const idUsuario = localStorage.getItem('id_usuario') ?? undefined;
            const body: Record<string, unknown> = {
                carteira: filters.carteira,
                dataAquisicao: filters.dt_aquisicao,
                ids: selectedIds,
                lastroIds: [],
                idUsuario: idUsuario,
            };
            const response = await fetch(
                `${API_CONFIG.BASE_URL_JAVA}/api/v1/lastros-contratos/download-lastros`,
                {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify(body),
                }
            );
            if (!response.ok) throw new Error('Erro ao exportar lastros');
            const json = await response.json();
            const downloadUrl = json?.downloadUrl;
            if (!downloadUrl) throw new Error('URL de download não encontrada na resposta');
            window.location.href = downloadUrl;
        } catch (err) {
            console.error('Erro ao exportar lastros:', err);
        } finally {
            setExportLoading(false);
        }
    };

    const abrirLink = (url: string) => {
        window.open(url, '_blank');
    };

    type DetalheItem = NonNullable<LastrosAtivos['lastros']>[number];

    const lastrosColumns = useMemo((): ColumnDef<LastrosAtivos>[] => [
        { key: 'numeroDocumento', label: 'Nº Documento', sortable: true },
        { key: 'nomeFundo', label: 'Fundo', sortable: true },
        { key: 'nomeCedente', label: 'Cedente', sortable: true },
        { key: 'nomeOriginador', label: 'Originador', sortable: true },
        {
            key: 'dataAquisicao',
            label: 'Data Aquisição',
            sortable: true,
            render: item => formatDate(item.dataAquisicao),
        },
        { key: 'status', label: 'Status', sortable: true },
        {
            key: 'tipoArmazenamento',
            label: 'Armazenamento',
            sortable: true,
            render: item => item.tipoArmazenamento?.toLowerCase().includes('interno') ? 'Interno' : item.tipoArmazenamento?.toLowerCase().includes('externo') ? 'Externo' : '-',
        },
    ], []);

    const detalhesColumns = useMemo((): ColumnDef<DetalheItem>[] => [
        {
            key: 'select',
            label: '',
            headerRender: () => {
                const allIds = selectedItem?.lastros?.map(l => l.lastroId) ?? [];
                const allSelected = allIds.length > 0 && allIds.every(id => selectedLastroIds.includes(id));
                return (
                    <input
                        type="checkbox"
                        className="lastro-checkbox"
                        checked={allSelected}
                        onChange={() => canDownload && setSelectedLastroIds(allSelected ? [] : allIds)}
                        disabled={!canDownload}
                        title={!canDownload ? 'Sem permissão para selecionar' : allSelected ? 'Desmarcar todos' : 'Selecionar todos'}
                    />
                );
            },
            render: d => (
                <input
                    type="checkbox"
                    className="lastro-checkbox"
                    checked={selectedLastroIds.includes(d.lastroId)}
                    onChange={() => canDownload && setSelectedLastroIds(prev =>
                        prev.includes(d.lastroId)
                            ? prev.filter(id => id !== d.lastroId)
                            : [...prev, d.lastroId]
                    )}
                    disabled={!canDownload}
                    title={canDownload ? 'Selecionar para exportar' : 'Sem permissão para selecionar'}
                />
            ),
        },
        { key: 'nomeArquivo', label: 'Arquivo' },
        {
            key: 'dataUpload',
            label: 'Data de depósito',
            render: d => d.dataUpload
                ? new Date(d.dataUpload).toLocaleString('pt-BR')
                : '-',
        },
    ], [selectedLastroIds, selectedItem]);

    const statusOptions: SelectOption[] = [
        { value: '', label: 'Todos os status' },
        { value: 'DISPONIVEL', label: 'Disponível' },
        { value: 'SEM_LASTRO', label: 'Sem Lastro' },
    ];

    return (
        <PermissionGate required="VIEW_BACKINGS">
            <ErrorBoundary>
                <div className="registros-container">
                    <FiltersCard
                        actions={
                            <>
                                <Button onClick={clearFilters} variant="secondary" icon={faRefresh}>Limpar</Button>
                                <Button onClick={handleSearch} variant="primary" icon={faSearch}>Buscar</Button>
                            </>
                        }
                    >
                        <Select
                            label="Carteira"
                            value={filters.carteira}
                            onChange={v => handleFilterChange('carteira', v)}
                            options={fundoOptions}
                            placeholder="Todos os fundos"
                        />
                        <MultiSelect
                            label="Cedente"
                            values={filters.cedente}
                            onChange={v => handleFilterChange('cedente', v)}
                            options={cedenteOptions}
                            placeholder="Todos os cedentes"
                        />
                        <MultiSelect
                            label="Originador"
                            values={filters.originador}
                            onChange={v => handleFilterChange('originador', v)}
                            options={originadorOptions}
                            placeholder="Todos os originadores"
                        />
                        <DatePicker
                            label="Data de Aquisição"
                            value={filters.dt_aquisicao}
                            onChange={v => handleFilterChange('dt_aquisicao', v || new Date().toISOString().split('T')[0])}
                            isLastroDate
                        />
                        <TagInput
                            label="Número do Contrato"
                            values={filters.numero_contrato}
                            onChange={v => handleFilterChange('numero_contrato', v)}
                            placeholder="Digite e pressione Enter ou vírgula"
                        />
                        <Select
                            label="Status"
                            value={filters.status}
                            onChange={v => handleFilterChange('status', v)}
                            options={statusOptions}
                            placeholder="Todos os status"
                        />
                    </FiltersCard>

                    <div className="results-section">

                        {data.length > 0 && (() => {
                            const filtersOk = !!filters.dt_aquisicao;
                            const selectionOk = selectedIds.length > 0;
                            const allSemLastro = selectionOk && selectedIds.every(id => selectedStatuses[id] === 'SEM_LASTRO');
                            const limitExceeded = selectedIds.length > 100;
                            const canExportPdf = filtersOk && selectionOk && !selectingAllIds && !allSemLastro && !limitExceeded;
                            const pdfTitle = !selectionOk
                                ? 'Selecione contratos para baixar (máx 100)'
                                : allSemLastro
                                    ? 'Todos os contratos selecionados estão sem lastro'
                                    : limitExceeded
                                        ? `Download suporta no máximo 100 contratos (${selectedIds.length} selecionados)`
                                        : `Baixar ${selectedIds.length} contrato(s) como PDF`;
                            return (
                                <div className="liquidar-actions-bar">
                                    <span className="liquidar-selected-count">
                                        {selectingAllIds
                                            ? 'Selecionando todos os lastros...'
                                            : selectionOk
                                                ? `${selectedIds.length} ${selectedIds.length === 1 ? 'lastro selecionado' : 'lastros selecionados'}${selectedIds.length === totalElements ? ' (todos)' : ''}`
                                                : 'Nenhum lastro selecionado'}
                                    </span>

                                    <button
                                        className="liquidar-button"
                                        onClick={() => API_CONFIG.POWERBI_URL && abrirLink(API_CONFIG.POWERBI_URL)}
                                        disabled={!filtersOk || biLoading || !API_CONFIG.POWERBI_URL}
                                        title={
                                            !filtersOk ? 'Abrir Power BI' : !API_CONFIG.POWERBI_URL ? 'Power BI URL não configurada' : undefined}
                                    >
                                        <FontAwesomeIcon icon={biLoading ? faSpinner : faChartColumn} spin={biLoading} />
                                        {biLoading ? 'Abrindo...' : 'Abrir Power BI'}
                                    </button>

                                    <button
                                        className="liquidar-button"
                                        onClick={exportCsvAll}
                                        disabled={!filtersOk || exportCsvLoading}
                                        title={filtersOk
                                            ? selectionOk
                                                ? `Exportar ${selectedIds.length} registro(s) selecionado(s) como Excel`
                                                : 'Exportar todos os registros filtrados como Excel'
                                            : 'Informe a Data de Aquisição para exportar'}
                                    >
                                        <FontAwesomeIcon icon={exportCsvLoading ? faSpinner : faFileCsv} spin={exportCsvLoading} />
                                        {exportCsvLoading ? 'Exportando...' : 'Exportar Relatório'}
                                    </button>
                                    <PermissionGate required="DOWNLOAD_BACKINGS">
                                        <button
                                            className="liquidar-button"
                                            onClick={exportLastrosPorContrato}
                                            disabled={!canExportPdf || exportLoading}
                                            title={pdfTitle}
                                        >
                                            <FontAwesomeIcon icon={exportLoading ? faSpinner : faFilePdf} spin={exportLoading} />
                                            {exportLoading ? 'Baixando...' : 'Download Lastros'}
                                        </button>
                                    </PermissionGate>
                                </div>
                            );
                        })()}

                        <DataTable<LastrosAtivos>
                            data={data}
                            columns={lastrosColumns}
                            loading={loading}
                            sortField={sortField}
                            sortDirection={sortDirection}
                            onSort={handleSort}
                            rowKey={item => String(item.id)}
                            rowSelection={{
                                selectedIds,
                                onSelect: handleSelectItem,
                                onSelectAll: handleSelectAllPdf,
                                isSelectable: () => canDownload && !!filters.dt_aquisicao,
                                disabledTitle: !canDownload ? 'Sem permissão para selecionar lastros' : 'Informe a Data de Aquisição para selecionar',
                                getRowId: item => item.id,
                                selectAllDisabled: selectingAllIds,
                                totalCount: totalElements,
                            }}
                            loadingText="Carregando dados..."
                            emptyTitle="Nenhum lastro encontrado"
                            emptyMessage="Nenhuma informação de lastro encontrada com os filtros aplicados."
                            emptyHint="Use os filtros acima para buscar informações específicas."
                            renderActions={item => {
                                const hasLastros = item.lastros && item.lastros.length > 0;
                                return (
                                    <button
                                        className="view-button"
                                        onClick={() => hasLastros && handleOpenModal(item)}
                                        disabled={!hasLastros}
                                        title={hasLastros ? 'Ver arquivos de lastro' : 'Sem arquivos de lastro'}
                                    >
                                        <FontAwesomeIcon icon={faEye} />
                                    </button>
                                );
                            }}
                            actionsLabel="Arquivos"
                        />

                        {totalPages > 0 && (
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                pageSize={pageSize}
                                totalElements={totalElements}
                                onPageChange={handlePageChange}
                                onPageSizeChange={handlePageSizeChange}
                            />
                        )}
                    </div>
                </div>

                {modalOpen && selectedItem && (
                    <div
                        className="modal-overlay"
                        onClick={() => {
                            setModalOpen(false);
                            setSelectedItem(null);
                        }}
                    >
                        <div
                            className="modal-content"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="modal-header">
                                <h3 className="modal-title">
                                    Detalhes do Lastro - Contrato {selectedItem.numeroDocumento}
                                </h3>
                                <button
                                    className="modal-close"
                                    onClick={() => {
                                        setModalOpen(false);
                                        setSelectedItem(null);
                                    }}
                                    title="Fechar"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>

                            <div className="modal-body">
                                <DataTable
                                    data={selectedItem.lastros ?? []}
                                    columns={detalhesColumns}
                                    rowKey={d => d.lastroId}
                                    emptyTitle="Sem documentos"
                                    emptyMessage="Nenhum arquivo disponível para este lastro."
                                />
                            </div>

                            <div className="modal-footer">
                                <span className="modal-footer-info">
                                    {selectedLastroIds.length > 0
                                        ? `${selectedLastroIds.length} arquivo(s) selecionado(s)`
                                        : 'Selecione ao menos um arquivo para exportar'}
                                </span>

                                <PermissionGate required="DOWNLOAD_BACKINGS">
                                    <button
                                        className="liquidar-button"
                                        onClick={exportLastros}
                                        disabled={exportLastroLoading || selectedLastroIds.length === 0}
                                        title={selectedLastroIds.length === 0 ? 'Selecione ao menos um arquivo' : 'Exportar arquivos selecionados como PDF'}
                                    >
                                        <FontAwesomeIcon
                                            icon={exportLastroLoading ? faSpinner : faFilePdf}
                                            spin={exportLastroLoading}
                                        />
                                        {exportLastroLoading ? 'Exportando...' : 'Exportar Arquivos'}
                                    </button>
                                </PermissionGate>
                            </div>
                        </div>
                    </div>
                )}
            </ErrorBoundary>
        </PermissionGate>
    )
}

export default Lastros;